// src/pages/EdgeAgents.jsx
//
// Admin surface for Phase 2 per-agent identity.
// Backend: see docs/phase-2-agent-identity-design.md and
// backend/otsentry_backend/routers/agents_router.py.
//
// UX principles baked in:
//   1. Claim codes and newly-issued tokens are shown ONCE with a big
//      "copy to clipboard" affordance + the exact `.env` snippet the
//      operator pastes into the agent host. No backend-side "show me
//      again" — if they lose it, they provision a new one.
//   2. Every destructive action confirms with an optional reason field
//      that writes to `auth_audit_log.details` for the forensic trail.
//   3. Staleness is computed client-side from last_seen_at; the backend
//      doesn't need a cron to flip states.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import api from '../services/api';

const STALE_THRESHOLD_SECONDS = 15 * 60;

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const ageSeconds = (iso) => {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / 1000;
};

const isStale = (agent) => {
  if (agent.status !== 'active') return false;
  return ageSeconds(agent.last_seen_at) > STALE_THRESHOLD_SECONDS;
};

const STATUS_STYLES = {
  active:        { label: 'Active',        color: 'bg-green-900/40 text-green-300 border-green-700' },
  active_grace:  { label: 'Rotating',      color: 'bg-blue-900/40 text-blue-300 border-blue-700' },
  provisioned:   { label: 'Unclaimed',     color: 'bg-amber-900/40 text-amber-300 border-amber-700' },
  claim_expired: { label: 'Claim expired', color: 'bg-orange-900/40 text-orange-300 border-orange-700' },
  revoked:       { label: 'Revoked',       color: 'bg-gray-800 text-gray-500 border-gray-700' },
};

const StatusPill = ({ agent }) => {
  const stale = isStale(agent);
  const s = STATUS_STYLES[agent.status] || { label: agent.status, color: 'bg-gray-800 text-gray-400 border-gray-700' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${s.color}`}>
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
      {s.label}
      {stale && <span className="ml-1 text-amber-400" title="No heartbeat in >15 min">⚠ stale</span>}
    </span>
  );
};

// ──────────────────────────────────────────────────────────────────────
// One-time secret modal — reused for new claim codes AND rotated tokens.
// ──────────────────────────────────────────────────────────────────────

const OneTimeSecretModal = ({ title, subtitle, secret, secretLabel, envSnippet, expiresAt, extraWarning, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [remainingSec, setRemainingSec] = useState(null);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const left = Math.max(0, (new Date(expiresAt).getTime() - Date.now()) / 1000);
      setRemainingSec(Math.floor(left));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const doCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fall through */ }
  };

  const hours = remainingSec !== null ? Math.floor(remainingSec / 3600) : null;
  const mins = remainingSec !== null ? Math.floor((remainingSec % 3600) / 60) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full p-6">
        <h2 className="text-xl font-semibold text-gray-100 mb-1">{title}</h2>
        {subtitle && <p className="text-sm text-gray-400 mb-4">{subtitle}</p>}

        <div className="bg-amber-900/30 border border-amber-700 text-amber-200 text-sm rounded px-3 py-2 mb-4">
          <strong>This {secretLabel} will not be shown again.</strong> Copy it now and hand it to whoever is deploying the agent.
          {extraWarning && <div className="mt-1 text-amber-300/80">{extraWarning}</div>}
        </div>

        <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">{secretLabel}</label>
        <div className="flex gap-2 mb-4">
          <code className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-100 font-mono break-all">
            {secret}
          </code>
          <button
            onClick={() => doCopy(secret)}
            className="shrink-0 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded px-3 py-2"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>

        {envSnippet && (
          <>
            <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
              Paste into the agent's <code className="text-gray-300">.env</code>
            </label>
            <div className="flex gap-2 mb-4">
              <pre className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-xs text-gray-100 font-mono overflow-x-auto whitespace-pre">
{envSnippet}
              </pre>
              <button
                onClick={() => doCopy(envSnippet)}
                className="shrink-0 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded px-3 py-2"
              >
                Copy
              </button>
            </div>
          </>
        )}

        {remainingSec !== null && (
          <div className="text-sm text-gray-400 mb-4">
            Expires in <span className="text-gray-200 font-medium">{hours > 0 ? `${hours}h ${mins}m` : `${mins}m`}</span>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="bg-gray-700 hover:bg-gray-600 text-white rounded px-4 py-2"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Provision modal — name + optional tenant_id → backend returns claim code
// ──────────────────────────────────────────────────────────────────────

const ProvisionModal = ({ onClose, onProvisioned }) => {
  const [name, setName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Agent name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const body = { name: name.trim() };
      if (tenantId.trim()) {
        const parsed = parseInt(tenantId, 10);
        if (Number.isNaN(parsed) || parsed <= 0) {
          setError('tenant_id must be a positive integer (or leave blank for a platform agent).');
          setSubmitting(false);
          return;
        }
        body.tenant_id = parsed;
      }
      const resp = await api.post('/agents/provision', body);
      onProvisioned(resp.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Provision failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-gray-900 border border-gray-700 rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-semibold text-gray-100 mb-1">Provision new agent</h2>
        <p className="text-sm text-gray-400 mb-5">
          Creates an agent record and generates a one-time claim code. Hand the code to whoever deploys the agent; the agent exchanges it on first boot for a long-lived Bearer token.
        </p>

        <div className="space-y-4 mb-4">
          <div>
            <label htmlFor="agent-name" className="block text-sm font-medium text-gray-300 mb-1">
              Agent name
            </label>
            <input
              id="agent-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. austin-booth-01"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={submitting}
              autoFocus
            />
            <div className="text-xs text-gray-500 mt-1">
              Human-readable label shown in the admin list. Cannot be changed later.
            </div>
          </div>

          <div>
            <label htmlFor="agent-tenant" className="block text-sm font-medium text-gray-300 mb-1">
              Tenant ID <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              id="agent-tenant"
              type="number"
              min="1"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="Leave blank for a platform agent"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={submitting}
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-900/40 border border-red-800 text-red-200 text-sm rounded px-3 py-2 mb-4">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="bg-gray-700 hover:bg-gray-600 text-white rounded px-4 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-400 text-white font-medium rounded px-4 py-2"
          >
            {submitting ? 'Provisioning…' : 'Provision + generate claim'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Revoke modal — confirm + optional reason
// ──────────────────────────────────────────────────────────────────────

const RevokeModal = ({ agent, onClose, onRevoked }) => {
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!confirm) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/agents/${agent.id}/revoke`, { reason: reason.trim() || undefined });
      onRevoked();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Revoke failed.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-gray-900 border border-gray-700 rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-semibold text-gray-100 mb-1">Revoke agent</h2>
        <p className="text-sm text-gray-400 mb-4">
          Revoking <span className="text-gray-200 font-mono">{agent.name}</span> takes effect immediately. Its next request will be rejected with <code>403 Credential revoked</code>. Other agents are unaffected.
        </p>

        <div className="space-y-4 mb-4">
          <div>
            <label htmlFor="revoke-reason" className="block text-sm font-medium text-gray-300 mb-1">
              Reason <span className="text-gray-500 font-normal">(optional, written to audit log)</span>
            </label>
            <input
              id="revoke-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. hardware retired, suspected compromise, rotation"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-red-500"
              disabled={submitting}
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
              className="mt-0.5"
              disabled={submitting}
            />
            <span>I understand this is immediate and cannot be undone. The persisted token on the agent becomes unusable.</span>
          </label>
        </div>

        {error && (
          <div className="bg-red-900/40 border border-red-800 text-red-200 text-sm rounded px-3 py-2 mb-4">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="bg-gray-700 hover:bg-gray-600 text-white rounded px-4 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!confirm || submitting}
            className="bg-red-600 hover:bg-red-500 disabled:bg-gray-700 disabled:text-gray-400 text-white font-medium rounded px-4 py-2"
          >
            {submitting ? 'Revoking…' : 'Revoke'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────

const EdgeAgents = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProvision, setShowProvision] = useState(false);
  const [provisionResult, setProvisionResult] = useState(null);  // {agent_id, claim_code, claim_expires_at}
  const [rotateResult, setRotateResult] = useState(null);        // {agent_id, agent_token, rotated_from_id, grace_ends_at}
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [actionInFlight, setActionInFlight] = useState(null);    // agent.id for per-row disable
  const [showRevoked, setShowRevoked] = useState(false);

  const loadAgents = useCallback(async () => {
    try {
      const resp = await api.get('/agents/');
      setAgents(resp.data || []);
      setError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to load agents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
    const id = setInterval(loadAgents, 30_000);  // auto-refresh for last_seen_at
    return () => clearInterval(id);
  }, [loadAgents]);

  const visibleAgents = useMemo(() => {
    if (showRevoked) return agents;
    return agents.filter((a) => a.status !== 'revoked');
  }, [agents, showRevoked]);

  const rotateAgent = async (agent) => {
    if (!window.confirm(`Rotate token for "${agent.name}"? The agent will have a 7-day grace window to pick up the new token before the old one stops working.`)) {
      return;
    }
    setActionInFlight(agent.id);
    try {
      const resp = await api.post(`/agents/${agent.id}/rotate`);
      setRotateResult(resp.data);
      await loadAgents();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      window.alert(typeof detail === 'string' ? detail : 'Rotate failed.');
    } finally {
      setActionInFlight(null);
    }
  };

  const reissueClaim = async (agent) => {
    if (!window.confirm(`Generate a fresh claim code for "${agent.name}"?`)) {
      return;
    }
    setActionInFlight(agent.id);
    try {
      const resp = await api.post(`/agents/${agent.id}/reissue-claim`);
      setProvisionResult(resp.data);
      await loadAgents();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      window.alert(typeof detail === 'string' ? detail : 'Re-issue failed.');
    } finally {
      setActionInFlight(null);
    }
  };

  // Admin-only page — defense in depth (the router also gates admin).
  if (user && user.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto mt-10 p-6 text-gray-300">
        <h1 className="text-2xl font-semibold text-gray-100 mb-2">Edge Agents</h1>
        <p className="text-sm text-gray-400">Admin-only page. Contact your administrator for access.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto mt-8 p-6">
      <div className="flex items-start justify-between mb-1">
        <h1 className="text-2xl font-semibold text-gray-100">Edge Agents</h1>
        <button
          onClick={() => setShowProvision(true)}
          className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded px-4 py-2"
        >
          + Provision agent
        </button>
      </div>
      <p className="text-sm text-gray-400 mb-5">
        Per-agent identity via one-time claim codes. See <code className="text-gray-300">docs/phase-2-agent-identity-design.md</code> for the full flow.
      </p>

      {error && (
        <div className="bg-red-900/40 border border-red-800 text-red-200 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 text-sm text-gray-400">
          <div>
            {loading ? 'Loading…' : `${visibleAgents.length} ${visibleAgents.length === 1 ? 'agent' : 'agents'}`}
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e) => setShowRevoked(e.target.checked)}
            />
            <span>Show revoked</span>
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/50 text-gray-400 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Version</th>
                <th className="text-left px-4 py-2 font-medium">Tenant</th>
                <th className="text-left px-4 py-2 font-medium">Last seen</th>
                <th className="text-left px-4 py-2 font-medium">Registered</th>
                <th className="text-right px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {visibleAgents.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                    No agents {showRevoked ? '' : 'active'}. Click <span className="text-blue-400">+ Provision agent</span> to create the first.
                  </td>
                </tr>
              )}
              {visibleAgents.map((a) => (
                <tr key={a.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-100">{a.name}</div>
                    <div className="text-xs text-gray-500 font-mono" title={a.id}>{a.id.slice(0, 8)}…{a.registered_hostname ? ` · ${a.registered_hostname}` : ''}</div>
                  </td>
                  <td className="px-4 py-2"><StatusPill agent={a} /></td>
                  <td className="px-4 py-2 text-xs font-mono text-gray-400">{a.registered_agent_version || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-400">{a.tenant_id ?? <span className="text-gray-500">platform</span>}</td>
                  <td className="px-4 py-2 text-xs text-gray-400">{fmtDate(a.last_seen_at)}</td>
                  <td className="px-4 py-2 text-xs text-gray-400">{fmtDate(a.registered_at)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {a.status === 'active' && (
                        <button
                          onClick={() => rotateAgent(a)}
                          disabled={actionInFlight === a.id}
                          className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-50"
                        >
                          Rotate
                        </button>
                      )}
                      {a.status === 'claim_expired' && (
                        <button
                          onClick={() => reissueClaim(a)}
                          disabled={actionInFlight === a.id}
                          className="text-xs px-2 py-1 rounded bg-amber-800/60 hover:bg-amber-700 text-amber-100 disabled:opacity-50"
                        >
                          Re-issue claim
                        </button>
                      )}
                      {a.status !== 'revoked' && (
                        <button
                          onClick={() => setRevokeTarget(a)}
                          disabled={actionInFlight === a.id}
                          className="text-xs px-2 py-1 rounded bg-red-900/50 hover:bg-red-800 text-red-100 disabled:opacity-50"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Provision flow */}
      {showProvision && (
        <ProvisionModal
          onClose={() => setShowProvision(false)}
          onProvisioned={(data) => {
            setShowProvision(false);
            setProvisionResult(data);
            loadAgents();
          }}
        />
      )}

      {/* One-time claim display (from provision OR reissue) */}
      {provisionResult && (
        <OneTimeSecretModal
          title="Claim code ready"
          subtitle="The agent will exchange this for a long-lived Bearer token on first boot."
          secret={provisionResult.claim_code}
          secretLabel="Claim code"
          envSnippet={`CLAIM_CODE=${provisionResult.claim_code}\nAGENT_TOKEN_PATH=/var/lib/ai-sentry-agent/agent_token`}
          expiresAt={provisionResult.claim_expires_at}
          extraWarning="Single-use. The first POST to /agents/register that presents this code consumes it."
          onClose={() => setProvisionResult(null)}
        />
      )}

      {/* One-time new-token display (from rotate) */}
      {rotateResult && (
        <OneTimeSecretModal
          title="Token rotated"
          subtitle={`New token issued. The old token remains valid for 7 days (until ${fmtDate(rotateResult.grace_ends_at)}), giving the running agent time to pick up the new token without downtime.`}
          secret={rotateResult.agent_token}
          secretLabel="New agent token"
          envSnippet={`AGENT_TOKEN=${rotateResult.agent_token}\nAGENT_TOKEN_PATH=/var/lib/ai-sentry-agent/agent_token`}
          extraWarning="After the 7-day grace window expires, the old token stops working."
          onClose={() => setRotateResult(null)}
        />
      )}

      {/* Revoke confirm */}
      {revokeTarget && (
        <RevokeModal
          agent={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoked={() => {
            setRevokeTarget(null);
            loadAgents();
          }}
        />
      )}
    </div>
  );
};

export default EdgeAgents;
